// A surface's rows on the Take Off: the surface itself, then each extra paint
// system on the same measurement (#412, docs/SURFACE-NAMES.md). Coats, rate
// and spread are this option's on both; a paint system's prep too, starting at
// none. A flagged surface, or one with paint systems, has a "Non-standard
// paint" label with a red "?" and edge until the flag is resolved (another
// paint or a paint system) and every system is done, then a green tick and
// edge; each system's row has its own edge. The measurer's note always stays
// (#666).
import { Plus, X } from 'lucide-react';
import { paintStatesComplete, paintSystemComplete, type PaintStates } from '@higgins/m2q-pricing';
import type { MeasuredSettings, PaintChoice, PaintSystemChoice, SurfaceChoice, SurfaceSetting, SystemSetting } from '@higgins/m2q-pricing';
import type { PricedSection, SectionInput } from '@higgins/m2q-pricing';
import { CompletionEdge, PaintStatePill } from './paint-completeness';
import { RemoveButton } from './remove-button';
import { SavedTextField, TEXT_MAX } from './saved-text-field';
import { PaintSelect, WorkRow } from './take-off-rows';

type PricedRow = PricedSection['surfaces'][number];
type InputRow = SectionInput['surfaces'][number];

const SURFACE_SETTINGS: readonly SurfaceSetting[] = ['coats', 'rate', 'spread'];
const SYSTEM_SETTINGS: readonly SystemSetting[] = ['coats', 'rate', 'spread', 'prep'];

interface SurfaceRowProps {
  priced: PricedRow;
  input: InputRow;
  /** The coats, rate and spread the surface has without this option's change (the placeholder when cleared). */
  measured: MeasuredSettings;
  choice: SurfaceChoice | undefined;
  paints: PaintChoice[];
  /** The measurer's note when the surface is flagged as non-standard paint ('' for no note); undefined when it isn't. */
  paintFlag: string | undefined;
  /** Its flag and paint systems, each done or not (surfacePaintStates). */
  states: PaintStates;
  onPaint: (key: string) => void;
  onSetting: (setting: SurfaceSetting, value: number | null) => void;
  onLeaveOut: () => void;
  onAddSystem: () => void;
}

export function SurfaceRow({ priced: s, input, measured, choice, paints, paintFlag, states, onPaint, onSetting, onLeaveOut, onAddSystem }: SurfaceRowProps) {
  const flag = `Non-standard paint${paintFlag ? `: ${paintFlag}` : ''}`;
  const label = (
    <>
      <span className="flex min-w-0 items-center gap-1">
        <button type="button" onClick={onLeaveOut} className="shrink-0 text-slate-400 hover:text-red-700" aria-label={`Leave ${s.name} out of this option`} title="Leave out of this option">
          <X className="h-3.5 w-3.5" />
        </button>
        <span className="min-w-0 flex-1 truncate" title={s.name}>
          {s.name} <span className="text-xs text-slate-500">{input.unit}</span>
          {input.unpriced && <span className="ml-1 text-xs italic">not priced: wash only</span>}
        </span>
        <PaintSelect value={s.paintId} paints={paints} changed={!!choice?.paint} label={`${s.name} paint`} onChange={onPaint} />
        <button
          type="button"
          onClick={onAddSystem}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-orange-300 text-orange-800 hover:bg-orange-50"
          aria-label={`Add a paint system to ${s.name}`}
          title="Add a paint system on this measurement"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </span>
      <PaintStatePill states={states} label={flag} className="mt-0.5 flex" />
    </>
  );
  const complete = paintStatesComplete(states);
  const shown = complete === undefined ? label : <CompletionEdge complete={complete}>{label}</CompletionEdge>;
  return <WorkRow label={shown} priced={s} input={input} values={choice ?? {}} placeholders={{ ...measured, prep: input.prep }} editable={SURFACE_SETTINGS} onSetting={onSetting} />;
}

interface SystemRowProps {
  priced: PricedRow;
  input: InputRow;
  system: PaintSystemChoice;
  /** The system's coats, rate and spread before this option's typed values. */
  measured: MeasuredSettings;
  paints: PaintChoice[];
  onPaint: (key: string) => void;
  onSetting: (setting: SystemSetting, value: number | null) => void;
  onRename: (name: string) => void | Promise<void>;
  onRemove: () => void;
}

export function SystemRow({ priced: s, input, system, measured, paints, onPaint, onSetting, onRename, onRemove }: SystemRowProps) {
  const label = (
    <CompletionEdge complete={paintSystemComplete(system, s.hours)}>
      <span className="flex min-w-0 items-center gap-1 pl-3">
        <span className="shrink-0 text-slate-400">↳</span>
        {/* An unnamed system's "Paint system 2" is the placeholder, so the box shows it still needs a name (#666). */}
        <SavedTextField compact value={system.name ?? ''} placeholder={s.name} onSave={onRename} maxLength={TEXT_MAX} label="Paint system name" className="h-6 w-0 min-w-0 flex-1" />
        <PaintSelect value={s.paintId} paints={paints} changed={!!system.paint} label={`${s.name} paint`} onChange={onPaint} width="w-28" />
        <RemoveButton
          question={`Remove the paint system ${s.name}? Its coats, rates and paint go from this option's price.`}
          onRemove={onRemove}
          label={`Remove paint system ${s.name}`}
          title="Remove this paint system"
          className="flex h-6 w-6 shrink-0 items-center justify-center text-slate-400 hover:text-red-700"
        >
          <X className="h-3.5 w-3.5" />
        </RemoveButton>
      </span>
    </CompletionEdge>
  );
  return <WorkRow label={label} priced={s} input={input} values={system} placeholders={{ ...measured, prep: 0 }} editable={SYSTEM_SETTINGS} onSetting={onSetting} shared />;
}
